import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { formatCurrencyInput } from '@components/calculators/shared/calculator-utils';

import styles from './styles.module.scss';
const {
  combined_input_container,
  combined_input_wrapper,
  input_field,
  input_label,
  left_input,
  right_input,
  percent_symbol
} = styles;

const CombinedInputField = ({
  label,
  leftInputName,
  rightInputName,
  defaultLeftValue,
  defaultRightValue,
  onLeftChange,
  onRightChange,
  onLeftBlur,
  onRightBlur,
  leftPlaceholder = '0',
  rightPlaceholder = '0'
}) => {
  const leftInputRef = useRef(null);
  const rightInputRef = useRef(null);

  const [leftValue, setLeftValue] = useState('');
  const [rightValue, setRightValue] = useState('');

  // Initialize with formatted default values
  useEffect(() => {
    if (defaultLeftValue) {
      const formatted = formatCurrencyInput(defaultLeftValue.toString());
      setLeftValue(formatted.displayValue);
    }
  }, [defaultLeftValue]);

  useEffect(() => {
    if (defaultRightValue !== undefined && defaultRightValue !== null) {
      // Just show the number without the % symbol
      setRightValue(defaultRightValue.toString());
    }
  }, [defaultRightValue]);

  const handleLeftInputChange = (e) => {
    const rawValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const formatted = formatCurrencyInput(rawValue, cursorPosition);

    setLeftValue(formatted.displayValue);

    // Set cursor position after state update
    setTimeout(() => {
      if (leftInputRef.current) {
        leftInputRef.current.setSelectionRange(formatted.cursorPosition, formatted.cursorPosition);
      }
    }, 0);

    if (onLeftChange) {
      onLeftChange(formatted.numericValue);
    }
  };

  const handleRightInputChange = (e) => {
    const rawValue = e.target.value;
    // Remove any non-numeric characters except decimal point
    const cleanValue = rawValue.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point and max 2 decimal places
    const parts = cleanValue.split('.');
    let finalValue = parts[0] || '';
    if (parts.length > 1) {
      finalValue += '.' + parts[1].slice(0, 2);
    }

    // Convert to number and cap at 100
    const numericValue = Math.min(100, parseFloat(finalValue) || 0);

    setRightValue(finalValue);

    if (onRightChange) {
      onRightChange(numericValue);
    }
  };

  const handleLeftInputBlur = () => {
    // Call parent's onBlur handler (for validation/clamping)
    // The useEffect will automatically sync the display when defaultLeftValue updates
    if (onLeftBlur) {
      onLeftBlur();
    }
  };

  const handleRightInputBlur = () => {
    // Call parent's onBlur handler (for validation/clamping)
    // The useEffect will automatically sync the display when defaultRightValue updates
    if (onRightBlur) {
      onRightBlur();
    }
  };

  return (
    <div className={combined_input_container}>
      {label && (
        <label className={input_label}>
          <span>{label}</span>
        </label>
      )}
      <div className={combined_input_wrapper}>
        <input
          ref={leftInputRef}
          type="text"
          className={classNames(input_field, left_input)}
          value={leftValue}
          onChange={handleLeftInputChange}
          onBlur={handleLeftInputBlur}
          placeholder={leftPlaceholder}
          name={leftInputName}
        />
        <input
          ref={rightInputRef}
          type="text"
          className={classNames(input_field, right_input)}
          value={rightValue}
          onChange={handleRightInputChange}
          onBlur={handleRightInputBlur}
          placeholder={rightPlaceholder}
          name={rightInputName}
        />
        <span className={percent_symbol}>%</span>
      </div>
    </div>
  );
};

CombinedInputField.propTypes = {
  label: PropTypes.string,
  leftInputName: PropTypes.string,
  rightInputName: PropTypes.string,
  defaultLeftValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultRightValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLeftChange: PropTypes.func,
  onRightChange: PropTypes.func,
  onLeftBlur: PropTypes.func,
  onRightBlur: PropTypes.func,
  leftPlaceholder: PropTypes.string,
  rightPlaceholder: PropTypes.string
};

export default CombinedInputField;
